import { defineComponent, Fragment, getCurrentInstance, h, onBeforeUnmount, onMounted, type VNode } from "vue";
import { moveNodesBefore } from "@/src/shared/utils/connectedNodeMove";
import type { SlotDefinition } from "../types/customElements";

export const HOST_SLOT_NODES_KEY = "__easy8SlotNodes";
export const HOST_MOUNTING_KEY = "__easy8Mounting";

export type CapturedSlotNodes = {
  /** Every light-DOM node owned by the host at capture time (transport scripts excluded). */
  lightDomNodes: Node[];
  hasDefaultSlot: boolean;
  defaultNodes: Node[];
  namedSlotNames: string[];
  namedNodes: Record<string, Node[]>;
};

type SlotNodesHost = HTMLElement & {
  [HOST_SLOT_NODES_KEY]?: CapturedSlotNodes;
  [HOST_MOUNTING_KEY]?: boolean;
  _slots?: Record<string, Node[]>;
};

type LightDomSlotParsing = { _parseSlots?: (this: SlotNodesHost) => void };

type SlotOutlets = Record<string, () => VNode[]>;

/** Nodes a projector placed at least once; they may be moved again even from outside the host (teleported outlets). */
const claimedNodes = new WeakSet<Node>();
/** Nodes this layer detached (sweep or outlet unmount) and may therefore insert again later. */
const releasedNodes = new WeakSet<Node>();

const emptySlotNodes = (): CapturedSlotNodes => ({
  lightDomNodes: [],
  hasDefaultSlot: false,
  defaultNodes: [],
  namedSlotNames: [],
  namedNodes: {},
});

const slotNameOf = (node: Node): string | null => (node instanceof Element ? node.getAttribute("slot") : null);

const isDefaultSlotContent = (node: Node): boolean =>
  node.nodeType === Node.TEXT_NODE ? Boolean(node.textContent?.trim()) : node instanceof Element;

const collectSlotNode = (captured: Pick<CapturedSlotNodes, "defaultNodes" | "namedNodes">, node: Node): void => {
  const slotName = slotNameOf(node);

  if (slotName) {
    (captured.namedNodes[slotName] ??= []).push(node);
  } else if (node.nodeType !== Node.COMMENT_NODE) {
    captured.defaultNodes.push(node);
  }
};

const matchesSlotDefinition = (name: string, definition: SlotDefinition): boolean =>
  typeof definition === "string" ? definition === name : new RegExp(definition.source, definition.flags).test(name);

const resolveNamedSlots = (hostNamedSlots: string[], definitions: SlotDefinition[] = []): string[] =>
  hostNamedSlots.filter(name => definitions.some(definition => matchesSlotDefinition(name, definition)));

/** Collects references to the host's light-DOM slot nodes. Nothing is cloned or detached. */
export const captureSlotNodes = (host: HTMLElement, transportScripts: HTMLScriptElement[]): CapturedSlotNodes => {
  const scripts: Node[] = transportScripts;
  const lightDomNodes = Array.from(host.childNodes).filter(node => !scripts.includes(node));
  const captured = { defaultNodes: [] as Node[], namedNodes: {} as Record<string, Node[]> };

  lightDomNodes.forEach(node => collectSlotNode(captured, node));

  return {
    lightDomNodes,
    ...captured,
    hasDefaultSlot: captured.defaultNodes.some(isDefaultSlotContent),
    namedSlotNames: Object.keys(captured.namedNodes),
  };
};

export const readCapturedSlotNodes = (host: HTMLElement): CapturedSlotNodes =>
  (host as SlotNodesHost)[HOST_SLOT_NODES_KEY] ?? emptySlotNodes();

/**
 * Never-projected nodes may come from anywhere (including a light DOM someone else already stripped). Projected
 * nodes are moved again only while connected or after this layer detached them, so content removed by other code
 * (a Turbo Stream remove, a dismiss button) stays removed.
 */
const isProjectable = (host: HTMLElement, node: Node): boolean => {
  if (host.contains(node) || releasedNodes.has(node)) return true;

  return claimedNodes.has(node) ? node.isConnected : node.parentNode === null;
};

const claimNode = (node: Node): void => {
  claimedNodes.add(node);
  releasedNodes.delete(node);
};

const releaseFromParent = (parent: Node | null | undefined, node: Node): void => {
  if (!parent || node.parentNode !== parent) return;

  parent.removeChild(node);
  releasedNodes.add(node);
};

/**
 * Each slot has a stable projector definition and one placement owner: its last mounted end anchor. Slot calls
 * used only for content inspection never register an anchor. Distinct definitions prevent cross-slot Vue reuse
 * without keys, so repeated outlets for the same slot remain valid even under a shared parent.
 */
const projectorOutlet = (host: HTMLElement, nodes: Node[]): (() => VNode[]) => {
  const anchors = new Set<Node>();
  const owner = (): Node | undefined => Array.from(anchors).at(-1);
  let projected: Node[] = [];

  const project = (anchor: Node): void => {
    if (!anchor.parentNode) return;

    projected = nodes.filter(node => isProjectable(host, node));
    projected.forEach(claimNode);
    moveNodesBefore(projected, anchor);
  };

  const unmount = (anchor: Node): void => {
    const wasOwner = owner() === anchor;
    anchors.delete(anchor);
    if (!wasOwner) return;

    const survivor = owner();
    // Hand over before Vue removes the old outlet, keeping connected frames' requests alive.
    if (survivor) project(survivor);
    else projected.forEach(node => releaseFromParent(anchor.parentNode, node));
  };

  const projector = defineComponent({
    name: "Easy8SlotNodeProjector",
    setup() {
      const instance = getCurrentInstance();
      let anchor: Node | undefined;

      onMounted(() => {
        anchor = instance?.subTree.anchor as Node | undefined;
        if (!anchor) return;

        anchors.add(anchor);
        project(anchor);
      });
      onBeforeUnmount(() => {
        if (anchor) unmount(anchor);
      });

      // The fragment's start anchor lets Vue insert sibling outlets before this outlet's external content.
      return () => h(Fragment, []);
    },
  });

  return () => [h(projector)];
};

/** Builds slot render functions that project the captured host nodes into the wrapped component's outlets. */
export const buildSlotOutlets = (
  host: HTMLElement,
  captured: CapturedSlotNodes,
  definitions?: SlotDefinition[]
): SlotOutlets => {
  const entries: [string, Node[]][] = [
    ["default", captured.hasDefaultSlot ? captured.defaultNodes : []],
    ...resolveNamedSlots(captured.namedSlotNames, definitions).map((name): [string, Node[]] => [
      name,
      captured.namedNodes[name] ?? [],
    ]),
  ];

  return Object.fromEntries(
    entries.filter(([, nodes]) => nodes.length > 0).map(([name, nodes]) => [name, projectorOutlet(host, nodes)])
  );
};

/**
 * Removes light-DOM nodes no projector claimed (unmatched named slots, comments, whitespace) once the real component
 * has mounted. Released nodes stay referenced, so an outlet rendered later can still move them in.
 */
export const detachUnprojectedSlotNodes = (host: HTMLElement): void => {
  for (const node of readCapturedSlotNodes(host).lightDomNodes) {
    if (node.parentNode === host && !claimedNodes.has(node)) releaseFromParent(host, node);
  }
};

const clearHostExceptSlotNodes = (host: HTMLElement): void => {
  const preserved = new Set(readCapturedSlotNodes(host).lightDomNodes);

  for (const node of Array.from(host.childNodes)) {
    if (!preserved.has(node)) host.removeChild(node);
  }
};

/**
 * Wrapper-mode overrides of Vue 3.5 custom element internals:
 * - `_parseSlots` must not strip the light DOM on first connect; the wrapper projects the original nodes instead.
 * - `app.mount()` empties the container (`textContent = ""`); while the host mounts, spare the captured slot nodes.
 * A Vue upgrade that renames `_parseSlots` is reported; projection then still works, but frames reload once.
 */
export const installSlotNodeHostOverrides = (prototype: HTMLElement): void => {
  const lightDomParsing = prototype as unknown as LightDomSlotParsing;
  if (typeof lightDomParsing._parseSlots !== "function") {
    console.warn("[easy8 custom elements] Vue custom element internals changed; slot nodes may be detached on mount");
  }
  lightDomParsing._parseSlots = function parseSlots() {
    this._slots = {};
  };

  const nativeTextContent = Object.getOwnPropertyDescriptor(Node.prototype, "textContent");
  Object.defineProperty(prototype, "textContent", {
    configurable: true,
    get(this: SlotNodesHost) {
      return nativeTextContent?.get?.call(this) ?? null;
    },
    set(this: SlotNodesHost, value: string | null) {
      if (this[HOST_MOUNTING_KEY] && value === "") clearHostExceptSlotNodes(this);
      else nativeTextContent?.set?.call(this, value);
    },
  });
};
